import React, { useState, useEffect } from "react";
import { format, addDays, isSameDay, startOfWeek, endOfWeek, isAfter, startOfDay } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MonthPicker } from "@/components/ui/month-picker";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { TimeTrackingCalendarProps, CalendarDayStatus } from '@/types/time-tracking';
import { createShiftStatusService } from '@/lib/scheduling/shift-status-service';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';
import { useBusinessCreationValidation } from '@/lib/business';

const TimeTrackingCalendarCard: React.FC<TimeTrackingCalendarProps> = ({
  selectedDate,
  onDateSelect,
  business,
  refreshTrigger = 0,
  dayShifts,
  loadingShifts,
  employees,
  loadingEmployees,
  onShiftCreated,
  className
}) => {
  const [currentViewMonth, setCurrentViewMonth] = useState(selectedDate);
  const [calendarData, setCalendarData] = useState<{ [key: string]: CalendarDayStatus }>({});
  const [isLoading, setIsLoading] = useState(false);

  // Use centralized month availability service
  const {
    isMonthNavigationDisabled,
  } = useBusinessCreationValidation(business);

  // Fetch calendar data for the current month
  useEffect(() => {
    const fetchCalendarData = async () => {
      if (!business?.id) {
        setCalendarData({});
        return;
      }

      setIsLoading(true);
      const startDate = new Date(currentViewMonth.getFullYear(), currentViewMonth.getMonth(), 1);
      const endDate = new Date(currentViewMonth.getFullYear(), currentViewMonth.getMonth() + 1, 0);
      // Set endDate to end of day to include shifts that end on the last day of month
      endDate.setHours(23, 59, 59, 999);

      try {
        // Smart table routing: only query the tables we need based on date range
        const todayDate = startOfDay(new Date());
        let allShifts: any[] = [];

        // Determine which table(s) to query based on date range
        if (isAfter(startDate, todayDate)) {
          // Future month: only query current shifts table
          const { data: shiftsData, error: shiftsError } = await supabase
            .from('shifts')
            .select('id, start_time, end_time, employee_id')
            .eq('business_id', business.id)
            .gte('start_time', startDate.toISOString())
            .lte('start_time', endDate.toISOString());

          if (shiftsError) throw shiftsError;
          allShifts = shiftsData || [];

        } else if (!isAfter(endDate, todayDate)) {
          // Past month: only query historical shifts table
          const { data: historicalShiftsData, error: historicalShiftsError } = await supabase
            .from('shifts_history')
            .select('id, start_time, end_time, employee_id')
            .eq('business_id', business.id)
            .gte('start_time', startDate.toISOString())
            .lte('start_time', endDate.toISOString());

          if (historicalShiftsError) throw historicalShiftsError;
          allShifts = historicalShiftsData || [];

        } else {
          // Current month (spans past and present): query both tables
          const [currentResult, historicalResult] = await Promise.all([
            supabase
              .from('shifts')
              .select('id, start_time, end_time, employee_id')
              .eq('business_id', business.id)
              .gte('start_time', startDate.toISOString())
              .lte('start_time', endDate.toISOString()),
            supabase
              .from('shifts_history')
              .select('id, start_time, end_time, employee_id')
              .eq('business_id', business.id)
              .gte('start_time', startDate.toISOString())
              .lte('start_time', endDate.toISOString())
          ]);

          if (currentResult.error) throw currentResult.error;
          if (historicalResult.error) throw historicalResult.error;
          
          allShifts = [...(currentResult.data || []), ...(historicalResult.data || [])];
        }

        // Fetch time logs for the date range
        const { data: timeLogsData, error: timeLogsError } = await supabase
          .from('time_logs')
          .select('id, employee_id, event_type, scanned_at')
          .eq('business_id', business.id)
          .gte('scanned_at', startDate.toISOString())
          .lte('scanned_at', endDate.toISOString());

        if (timeLogsError) throw timeLogsError;

        // Calculate status for each day
        const dayStatus: { [key: string]: CalendarDayStatus } = {};
        
        // Initialize all days in current month
        for (let d = new Date(startDate); d <= endDate; d = addDays(d, 1)) {
          const dayKey = format(d, 'yyyy-MM-dd');
          dayStatus[dayKey] = { status: 'gray', shiftsCount: 0, timeLogsCount: 0 };
        }

        // Process shifts by date
        const shiftsByDate: { [key: string]: any[] } = {};
        allShifts.forEach(shift => {
          const shiftDate = format(new Date(shift.start_time), 'yyyy-MM-dd');
          if (!shiftsByDate[shiftDate]) shiftsByDate[shiftDate] = [];
          shiftsByDate[shiftDate].push(shift);
        });

        // Process time logs by date
        const timeLogsByDate: { [key: string]: any[] } = {};
        (timeLogsData || []).forEach(log => {
          const logDate = format(new Date(log.scanned_at), 'yyyy-MM-dd');
          if (!timeLogsByDate[logDate]) timeLogsByDate[logDate] = [];
          timeLogsByDate[logDate].push(log);
        });

        // Calculate status for each day based on attendance history and errors
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of today to include current day
        
        for (let d = new Date(startDate); d <= endDate; d = addDays(d, 1)) {
          const dayKey = format(d, 'yyyy-MM-dd');
          const dayShifts = shiftsByDate[dayKey] || [];
          const dayTimeLogs = timeLogsByDate[dayKey] || [];
          const isFutureDay = d > today;

          if (dayShifts.length === 0) {
            dayStatus[dayKey] = { status: 'gray', shiftsCount: 0, timeLogsCount: dayTimeLogs.length };
            continue;
          }

          // For future days with scheduled shifts, always show blue
          if (isFutureDay) {
            dayStatus[dayKey] = { 
              status: 'blue', 
              shiftsCount: dayShifts.length, 
              timeLogsCount: dayTimeLogs.length 
            };
            continue;
          }

          // For past/current days, analyze attendance history and detect errors
          const employeesWithShifts = [...new Set(dayShifts.map(s => s.employee_id).filter(Boolean))];
          const employeesWithArrivals = [...new Set(dayTimeLogs.filter(l => l.event_type === 'arrival').map(l => l.employee_id))];
          const employeesWithDepartures = [...new Set(dayTimeLogs.filter(l => l.event_type === 'departure').map(l => l.employee_id))];

          let status: CalendarDayStatus['status'] = 'red';
          let hasCriticalIssues = false;

          // Check for critical issues first
          if (employeesWithShifts.length === 0) {
            hasCriticalIssues = true; // Unassigned shifts
          } else if (employeesWithArrivals.length === 0 && employeesWithShifts.length > 0) {
            hasCriticalIssues = true; // No one showed up
          }

          // Analyze attendance issues using business-specific shift status service
          let attendanceIssues = 0;
          let criticalIssues = 0;

          // Create business-specific shift status service with proper timezone
          const businessShiftStatusService = createShiftStatusService({}, business?.timezone || 'UTC');

          for (const shift of dayShifts) {
            if (!shift.employee_id) {
              criticalIssues++; // Unassigned shift
              continue;
            }

            // Find arrival and departure logs for this employee on this day
            const arrivalLog = dayTimeLogs.find(log => 
              log.employee_id === shift.employee_id && log.event_type === 'arrival'
            );
            const departureLog = dayTimeLogs.find(log => 
              log.employee_id === shift.employee_id && log.event_type === 'departure'
            );

            // Use business-specific shift status service to get accurate status
            const statusResult = businessShiftStatusService.calculateDetailedShiftStatus(
              new Date(shift.start_time),
              new Date(shift.end_time),
              shift.employee_id,
              arrivalLog?.scanned_at || null,
              departureLog?.scanned_at || null
            );

            // Categorize issues based on shift status
            switch (statusResult.status) {
              case ShiftStatus.MISSED:
                criticalIssues++;
                break;
              case ShiftStatus.LATE:
              case ShiftStatus.LEFT_EARLY:
              case ShiftStatus.TOO_EARLY:
              case ShiftStatus.COMPLETED_LATE:
                attendanceIssues++;
                break;
              case ShiftStatus.PENDING:
                // Incomplete tracking for past days should be flagged
                if (!isFutureDay) {
                  attendanceIssues++;
                }
                break;
            }
          }

          // Set status based on analysis using shift status service results
          if (hasCriticalIssues || criticalIssues > 0) {
            status = 'red'; // Critical issues: unassigned shifts, no-shows
          } else if (attendanceIssues > 0) {
            status = 'orange'; // Attendance issues: late arrivals, early departures, incomplete tracking
          } else if (employeesWithArrivals.length === employeesWithShifts.length && 
                     employeesWithDepartures.length === employeesWithShifts.length) {
            status = 'green'; // Complete and on-time attendance
          } else {
            status = 'orange'; // Partial tracking or other minor issues
          }

          dayStatus[dayKey] = {
            status,
            shiftsCount: dayShifts.length,
            timeLogsCount: dayTimeLogs.length
          };
        }

        setCalendarData(dayStatus);
      } catch (err) {
        console.error('Error fetching calendar data:', err);
        setCalendarData({});
      } finally {
        setIsLoading(false);
      }
    };

    fetchCalendarData();
  }, [currentViewMonth, business?.id, refreshTrigger]);

  const goToPreviousMonth = () => {
    const currentMonth = currentViewMonth.getMonth();
    const currentYear = currentViewMonth.getFullYear();
    
    if (!isMonthNavigationDisabled(currentMonth, currentYear, 'previous')) {
      const prevMonth = new Date(currentViewMonth);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      setCurrentViewMonth(prevMonth);
    }
  };

  const goToNextMonth = () => {
    const currentMonth = currentViewMonth.getMonth();
    const currentYear = currentViewMonth.getFullYear();
    
    if (!isMonthNavigationDisabled(currentMonth, currentYear, 'next')) {
      const nextMonth = new Date(currentViewMonth);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setCurrentViewMonth(nextMonth);
    }
  };

  // Month change handler for MonthPicker
  const handleMonthChange = (newDate: Date) => {
    setCurrentViewMonth(newDate);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const monthStart = new Date(currentViewMonth.getFullYear(), currentViewMonth.getMonth(), 1);
    const monthEnd = new Date(currentViewMonth.getFullYear(), currentViewMonth.getMonth() + 1, 0);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = [];

    for (let d = new Date(calendarStart); d <= calendarEnd; d = addDays(d, 1)) {
      const dayKey = format(d, 'yyyy-MM-dd');
      const dayData = calendarData[dayKey] || { status: 'gray', shiftsCount: 0, timeLogsCount: 0 };
      const isCurrentMonth = d.getMonth() === currentViewMonth.getMonth();
      const isToday = isSameDay(d, new Date());
      const isSelected = isSameDay(d, selectedDate);
      const isFutureDay = d > new Date();

      days.push({
        date: new Date(d),
        dayKey,
        status: isCurrentMonth ? dayData.status : 'gray',
        shiftsCount: isCurrentMonth ? dayData.shiftsCount : 0,
        timeLogsCount: isCurrentMonth ? dayData.timeLogsCount : 0,
        isToday,
        isCurrentMonth,
        isSelected,
        isFutureDay
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  return (
    <Card className={`w-full ${className || ''}`}>
      <CardContent className="p-4">
        <div className="space-y-2">
          {/* Navigation, Month, Legend - All in one line */}
          <div className="flex items-center justify-between gap-4">
            {/* Month Navigation - Left side */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousMonth}
                disabled={isMonthNavigationDisabled(currentViewMonth.getMonth(), currentViewMonth.getFullYear(), 'previous')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <MonthPicker
                value={currentViewMonth}
                onChange={handleMonthChange}
                businessCreationDate={business}
                size="sm"
                className="font-semibold"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextMonth}
                disabled={isMonthNavigationDisabled(currentViewMonth.getMonth(), currentViewMonth.getFullYear(), 'next')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Legend */}
            <div className="hidden sm:grid grid-cols-2 gap-x-1 gap-y-1 text-xs flex-shrink-0 ml-2">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <span>Complete</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-orange-500" />
                <span>Issues</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-red-500" />
                <span>Critical</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <span>Upcoming</span>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          {isLoading ? (
            // Compact loading skeleton
            <div className="space-y-1">
              <div className="grid grid-cols-7 gap-1">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-xs text-center text-muted-foreground py-1 font-medium">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {[...Array(14)].map((_, i) => (
                  <Skeleton key={i} className="h-8 rounded-md" />
                ))}
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="text-xs text-center text-muted-foreground py-1 font-medium">
                {day}
              </div>
            ))}

            {/* Calendar Days - Always show exactly 42 cells (6 rows) */}
            {Array.from({ length: 42 }, (_, index) => {
              const day = calendarDays[index];
              if (!day) {
                // Empty cell for consistent 6-row layout
                return <div key={`empty-${index}`} className="h-8" />;
              }
              return (
              <div
                key={day.dayKey}
                className={`
                  relative h-8 rounded-md flex items-center justify-center text-xs font-medium cursor-pointer
                  transition-all duration-200 hover:scale-105 hover:shadow-sm
                  ${!day.isCurrentMonth ? 'text-gray-300 dark:text-gray-600 bg-gray-50/50 dark:bg-gray-800/30' : 
                    day.status === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                    day.status === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                    day.status === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                    day.status === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                    'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300'}
                  ${day.isToday ? 'ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-gray-900' : ''}
                  ${day.isSelected ? 'ring-2 ring-primary ring-offset-1 dark:ring-offset-gray-900 font-bold' : ''}
                `}
                title={
                  day.isFutureDay && day.shiftsCount > 0 
                    ? `${format(day.date, 'MMM d')}: ${day.shiftsCount} shifts scheduled`
                    : day.shiftsCount === 0
                    ? `${format(day.date, 'MMM d')}: No shifts scheduled`
                    : day.status === 'green'
                    ? `${format(day.date, 'MMM d')}: Complete attendance - all employees arrived on time and stayed for their full shift (${day.shiftsCount} shifts, ${day.timeLogsCount} logs)`
                    : day.status === 'orange'
                    ? `${format(day.date, 'MMM d')}: Attendance issues - late arrivals, early departures, or incomplete tracking (${day.shiftsCount} shifts, ${day.timeLogsCount} logs)`
                    : day.status === 'red'
                    ? `${format(day.date, 'MMM d')}: Critical problems - missed shifts, no-shows, or unassigned shifts (${day.shiftsCount} shifts, ${day.timeLogsCount} logs)`
                    : `${format(day.date, 'MMM d')}: ${day.shiftsCount} shifts, ${day.timeLogsCount} time logs`
                }
                onClick={() => onDateSelect(day.date)}
              >
                {format(day.date, 'd')}
                {day.shiftsCount > 0 && (
                  <div className="absolute -top-1 -right-1 h-3 w-3 bg-primary rounded-full text-[8px] text-primary-foreground flex items-center justify-center">
                    {day.shiftsCount > 9 ? '9+' : day.shiftsCount}
                  </div>
                )}
              </div>
              );
            })}
          </div>
          )}

          {/* Mobile Legend */}
          <div className="flex sm:hidden flex-col items-center gap-1 text-xs pt-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <span>Complete</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-orange-500" />
                <span>Issues</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-red-500" />
                <span>Critical</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <span>Upcoming</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TimeTrackingCalendarCard;